import React from 'react';
import { useParams } from 'react-router-dom';
import { useLDClient } from 'launchdarkly-react-client-sdk';

import { isSEOBot } from 'shared/helpers/utils';
import usePusher from 'src/hooks/use-pusher';
import { segmentIdentifyAndGroup } from 'shared/helpers/tools';
import { useStores } from 'src/hooks/use-stores';
import { useUser } from 'src/hooks/use-user';
import OrdersStore, { OrdersStoreProvider } from 'src/stores/orders';
import IntegrationsStore, { IntegrationsStoreContext } from 'src/stores/integrations';

type CreatedStores = {
  activeOrdersStore: InstanceType<typeof OrdersStore>;
  integrationsStore: InstanceType<typeof IntegrationsStore>;
};

type FeatureFlags = ReturnType<typeof useStores>['FeatureFlags'];
type LaunchDarklyClient = ReturnType<typeof useLDClient>;
type PusherStore = ReturnType<typeof usePusher>;
type RouteParams = { id: string };

function createStores(
  pusher: PusherStore,
  params: RouteParams,
  ldClient: LaunchDarklyClient,
  featureFlags: FeatureFlags
): CreatedStores {
  return {
    activeOrdersStore: new OrdersStore({
      dispensaryId: params.id,
      FeatureFlags: featureFlags,
      pusher,
      params: {
        status: 'active',
        perPage: 1000,
      },
    }),
    integrationsStore: new IntegrationsStore({
      dispensaryId: params.id,
      ldClient,
      FeatureFlags: featureFlags,
    }),
  };
}

export default function useCreateStores(): CreatedStores {
  const stores = React.useRef<CreatedStores>();
  const Pusher = usePusher();
  const User = useUser();
  const { FeatureFlags, UI } = useStores();
  const params = useParams<RouteParams>();
  const ldClient = useLDClient();

  // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- preserve legacy falsy initialization
  if (!stores.current) {
    stores.current = createStores(Pusher, params, ldClient, FeatureFlags);
  }
  const currentStores = stores.current;

  async function startStores(): Promise<void> {
    if (User.isSuperAdmin || User.user.profile?.permissions?.orders) {
      void currentStores.activeOrdersStore.start();
    }

    // dont attempt to configure segment until after integrations store has started
    await currentStores.integrationsStore.start();
    if (!isSEOBot()) {
      segmentIdentifyAndGroup(User._user, UI.dispensary, currentStores.integrationsStore.integrations);
    }
  }

  React.useEffect(() => {
    void startStores();

    return () => {
      currentStores.activeOrdersStore.destroy();
      currentStores.integrationsStore.stop();
    };
    // Preserve the legacy one-store-instance lifecycle rather than retriggering for callback identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStores]);

  return currentStores;
}

export function StoreWrapper({ children }: { children: React.ReactNode }): JSX.Element {
  const stores = useCreateStores();

  return (
    <OrdersStoreProvider value={{ active: stores.activeOrdersStore }}>
      <IntegrationsStoreContext.Provider value={stores.integrationsStore}>{children}</IntegrationsStoreContext.Provider>
    </OrdersStoreProvider>
  );
}
